<template>
  <div :id="modalId" class="product-modal" uk-modal>
    <div class="uk-modal-dialog" :class="{ 'uk-margin-auto-vertical': verticallyCenter }">
      <button class="uk-modal-close-default" type="button" uk-close></button>

      <div class="uk-modal-body">

        <div class="row">

          <div v-if="imageUrl && !useIconLayout" class="column">
            <a v-if="url" :href="url" target="_blank">
              <img :src="imageUrl" :title="name" :alt="name" />
            </a>
            <template v-else>
              <img :src="imageUrl" :title="name" :alt="name" />
            </template>
          </div>

          <div class="column">

            <template v-if="imageUrl && useIconLayout">
              {{ '' /** アイコンスタイル */ }}
              <div class="title-with-icon">
                <div v-if="imageUrl">
                  <img :src="imageUrl" />
                </div>
                <div>
                  <h2 v-if="kana">
                    <ruby>
                      {{ name }}
                      <rt>{{ kana }}</rt>
                    </ruby>
                  </h2>
                  <h2 v-else v-html="name"></h2>
                </div>
              </div>
            </template>
            <template v-else>
              {{ '' /** 通常スタイル */ }}
              <h2 v-if="kana">
                <ruby>
                  {{ name }}
                  <rt>{{ kana }}</rt>
                </ruby>
              </h2>
              <h2 v-else v-html="name"></h2>
            </template>

            <p v-if="kind" class="kind">{{ kind }}</p>
            <p v-if="description" class="description">{{ description }}</p>
          </div>
        </div>

        <div class="actions uk-text-center" v-if="showDetailButton">
          <a v-if="url" class="to-detail-page uk-button uk-button-primary uk-button-small" :href="url" target="_blank" v-wave>
            詳細ページへ
            <i class="material-symbols-sharp" data-icon="chevron_right"></i>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted } from 'vue'
  import uikit from 'uikit'

  let modal: uikit.UIkitModalElement | null = null

  const props = defineProps({
    modalId: {
      type: String,
      required: true
    },
    name: {
      type: String,
      required: false,
      default: null
    },
    kana: {
      type: String,
      required: false,
      default: null
    },
    kind: {
      type: String,
      required: false,
      default: null
    },
    description: {
      type: String,
      required: false,
      default: null
    },
    url: {
      type: String,
      required: false,
      default: null
    },
    imageUrl: {
      type: String,
      required: false,
      default: null
    },
    /**
     * モーダルを上下中央に配置
     * uikit
     */
    verticallyCenter: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * アイコンスタイルで表示するか
     * nameの左側にアイコン表示が表示される
     * imageUrl が存在する場合のみ有効
     */
    useIconLayout: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * 詳細ボタンを表示するか
     * true: ボタンを表示する
     */
    showDetailButton: {
      type: Boolean,
      required: false,
      default: true
    },
  })

  const modalId = computed(() => props.modalId)

  onMounted(() => {
    modal = uikit.modal(document.getElementById(modalId.value))
  })

  const open = () => {
    if (!modal) {
      return
    }
    modal.show()
  }

  defineExpose({
    open
  })
</script>

<style lang="scss" scoped>
  .uk-modal-dialog {
    width: 460px;
    margin-top: 200px;

    .uk-modal-body {
      padding: 0.75rem;

      .row {
        display: flex;
        width: 100%;

        .column {
          flex: 1 1 auto;
          padding: 1rem 0.75rem;

          .title-with-icon {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-right: 16px;
            img {
              max-width: 36px;
            }
          }

          img {
            max-height: 200px;
          }

          h2 {
            font-size: 1rem;
            margin-top: 0.25rem;
            margin-bottom: 0.25rem;

            ruby {
              rt {
                translate: 0 -0.25rem;
              }

              @-moz-document url-prefix() {
                rt {
                  position: relative;
                  top: -0.25rem;
                }
              }
            }
          }

          p {
            font-size: 0.875rem;
            margin-top: 0;
            margin-bottom: 0.5rem;

            &.kind {
              font-size: 0.75rem;
            }
          }

          &:first-child {
            flex: none;
            padding-right: 0;
          }
        }
      }

      .actions {
        .to-detail-page {
          position: relative;
          color: var(--text-color);
          border: 1px solid var(--text-color);
          border-radius: 1rem;
          padding: 0 2rem 0 1rem;

          i {
            display: inline;
            position: absolute;
            right: 0.5rem;
            top: 50%;
            transform: translateY(-50%);
            opacity: 0.7;
          }
        }
      }
    }

    @media (min-width: 640px) {
      width: 400px;
    }
  }
</style>
